import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './context/ThemeContext';
import { initializeEasyParcel } from './utils/easyParcelInit';
import { initializeWaha } from './utils/wahaInit';
import { initializeEmail } from './utils/emailInit';
import { followupScheduler } from './services/followupScheduler';

// Import components
import ProtectedRoute from "./components/ProtectedRoute";
import AppShell from "./components/layouts/AppShell";

import ErrorBoundary from './components/ErrorBoundary';

// Replaced legacy DashboardLayout with AppShell wrapper
const DashboardLayout = ({ children, isOnline }: { children: React.ReactNode, isOnline?: boolean }) => {
  return <AppShell>{children}</AppShell>;
};

// Lazy load components for better performance
const Login = lazy(() => import("./components/Login"));
const Dashboard = lazy(() => import("./components/Dashboard"));
const Orders = lazy(() => import("./components/Orders"));
const AddOrder = lazy(() => import("./components/AddOrder"));
const OrderHistory = lazy(() => import("./components/OrderHistory"));
const OrderDetail = lazy(() => import("./components/OrderDetail"));
const Products = lazy(() => import("./components/Products"));
const AddProduct = lazy(() => import("./components/AddProduct"));
const EditProduct = lazy(() => import("./components/EditProduct"));
const ProductView = lazy(() => import("./components/ProductView"));
const LabelOrder = lazy(() => import("./components/LabelOrder"));
const Inventory = lazy(() => import("./components/Inventory"));
const AddStaff = lazy(() => import("./components/AddStaff"));
const EditStaff = lazy(() => import("./components/EditStaff"));
const Communication = lazy(() => import("./components/Communication"));
const Documentation = lazy(() => import("./components/Documentation"));
const HelpAndSupport = lazy(() => import("./components/HelpAndSupport"));
const Settings = lazy(() => import("./components/Settings"));
const ReportProduct = lazy(() => import("./components/ReportProduct"));
const ReportInventory = lazy(() => import("./components/ReportInventory"));
const ReportSales = lazy(() => import("./components/ReportSales"));
const ReportOrder = lazy(() => import("./components/ReportOrder"));
const ReportReturn = lazy(() => import("./components/ReportReturn"));
const ProfitLoss = lazy(() => import("./components/ProfitLoss"));
const Expenses = lazy(() => import("./components/Expenses"));
const WebAnalytics = lazy(() => import("./components/WebAnalytics"));
const FinanceDashboard = lazy(() => import("./components/FinanceDashboard"));
const CashFlow = lazy(() => import("./components/CashFlow"));
const SystemCategories = lazy(() => import("./components/SystemCategories"));
const InvoiceReceipt = lazy(() => import("./components/InvoiceReceipt"));
const ReceiptList = lazy(() => import("./components/ReceiptList"));
const ReportCenter = lazy(() => import("./components/ReportCenter"));
const AuditLogReport = lazy(() => import("./components/AuditLogReport"));
const StaffManagement = lazy(() => import("./components/StaffManagement"));
const RoleManagement = lazy(() => import("./components/RoleManagement"));
const AccessManagement = lazy(() => import("./components/AccessManagement"));

// Integration components
const Integration = lazy(() => import("./components/Integration"));
const EmailIntegration = lazy(() => import("./components/EmailIntegration"));
const EmailTemplateBuilder = lazy(() => import("./components/EmailTemplateBuilder"));
const WooCommerceIntegration = lazy(() => import("./components/WooCommerceIntegration"));
const ShopeeIntegration = lazy(() => import("./components/ShopeeIntegration"));
const TiktokIntegration = lazy(() => import("./components/TiktokIntegration"));
const WahaIntegration = lazy(() => import("./components/WahaIntegration"));
const LadipageIntegration = lazy(() => import("./components/LadipageIntegration"));
const ApiIntegration = lazy(() => import("./components/ApiIntegration"));
const WebhookIntegration = lazy(() => import("./components/WebhookIntegration"));
const CourierIntegration = lazy(() => import('./components/CourierIntegration'));
const JnTIntegration = lazy(() => import('./components/JnTIntegration'));
const DHLIntegration = lazy(() => import('./components/DHLIntegration'));
const NinjaVanIntegration = lazy(() => import('./components/NinjaVanIntegration'));
const PosLajuIntegration = lazy(() => import('./components/PosLajuIntegration'));
const EasyParcelIntegration = lazy(() => import('./components/EasyParcelIntegration'));
const SpxIntegration = lazy(() => import('./components/SpxIntegration'));
const Storage = lazy(() => import('./components/Storage'));
const AutoSendManagement = lazy(() => import('./components/AutoSendManagement'));
const ComingSoon = lazy(() => import('./components/ComingSoon'));
const InventoryAnalytics = lazy(() => import('./components/InventoryAnalytics'));
const StockManagement = lazy(() => import('./components/StockManagement'));
const StockAdjustment = lazy(() => import('./components/StockAdjustment'));
const PurchaseOrders = lazy(() => import('./components/PurchaseOrders'));
const ProductsHub = lazy(() => import('./components/ProductsHub'));
const ProductsDashboard = lazy(() => import('./components/ProductsDashboard'));
const ProductCategories = lazy(() => import('./components/ProductCategories'));
const SalesAnalytics = lazy(() => import('./pages/sales/SalesV2'));
const NewDocumentPage = lazy(() => import('./pages/sales/NewDocumentPage'));
const AllDocuments = lazy(() => import('./components/AllDocuments'));
const Quotations = lazy(() => import('./components/Quotations'));
const Customers = lazy(() => import('./components/Customers'));
const Leads = lazy(() => import('./components/Leads'));
const PagesLibrary = lazy(() => import('./components/PagesLibrary'));
const XpertBuilder = lazy(() => import('./components/builder/XpertBuilder'));
const FormIntegrationSetup = lazy(() => import("./components/FormIntegrationSetup"));
const PublicForm = lazy(() => import("./components/PublicForm"));
const PaymentGatewayIntegration = lazy(() => import('./components/PaymentGatewayIntegration'));
const PreviewSalesStandalone = lazy(() => import('./components/sales/PreviewSalesStandalone'));

const App = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);


  // Check for online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initialize EasyParcel service from database
    initializeEasyParcel();
    // Initialize WAHA service
    initializeWaha();

    // Start Followup Scheduler ONLY when a token exists. Without
    // this check the scheduler hammers /api/v2/waha-followups every
    // 60s on the login screen and floods the console with
    // "Invalid or expired token". The scheduler now auto-stops on
    // auth errors; start() is re-invoked from the login success
    // handler.
    if (localStorage.getItem('crm_jwt')) {
      console.log('[App] Starting Followup Scheduler...');
      followupScheduler.start();
    } else {
      console.log('[App] No auth token — Followup Scheduler not started.');
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);



  return (
    <ThemeProvider>
      <Router>
        <ToastContainer />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#4ade80',
                secondary: '#fff',
              },
            },
            error: {
              duration: 4000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
        <ErrorBoundary>
          <Suspense fallback={
            <div className="flex items-center justify-center h-[80vh]">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-primary dark:border-dark-primary"></div>
            </div>
          }>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/preview-sales" element={<PreviewSalesStandalone />} />
              <Route path="/f/:formId" element={<PublicForm />} />

              {/* Dashboard as main component */}
              <Route path="/" element={
                <ProtectedRoute>
                  <DashboardLayout isOnline={isOnline}>
                    <Dashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardLayout isOnline={isOnline}>
                    <Dashboard />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/orders" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><Orders /></DashboardLayout></ProtectedRoute>} />
              <Route path="/orders/create" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><AddOrder /></DashboardLayout></ProtectedRoute>} />
              <Route path="/orders/edit/:id" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><AddOrder /></DashboardLayout></ProtectedRoute>} />
              <Route path="/orders/active" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><Orders /></DashboardLayout></ProtectedRoute>} />
              <Route path="/orders/returns" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><Orders /></DashboardLayout></ProtectedRoute>} />
              <Route path="/orders/history" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><OrderHistory /></DashboardLayout></ProtectedRoute>} />
              <Route path="/orders/:orderId" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><OrderDetail /></DashboardLayout></ProtectedRoute>} />
              <Route path="/products" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ProductsDashboard /></DashboardLayout></ProtectedRoute>} />
              <Route path="/products/catalog" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ProductsHub /></DashboardLayout></ProtectedRoute>} />
              <Route path="/products/list" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><Products /></DashboardLayout></ProtectedRoute>} />
              <Route path="/products/add" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><AddProduct /></DashboardLayout></ProtectedRoute>} />
              <Route path="/products/edit/:id" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><EditProduct /></DashboardLayout></ProtectedRoute>} />
              <Route path="/products/view/:id" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ProductView /></DashboardLayout></ProtectedRoute>} />
              <Route path="/products/labels" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><LabelOrder /></DashboardLayout></ProtectedRoute>} />
              <Route path="/products/inventory" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ProductsHub /></DashboardLayout></ProtectedRoute>} />
              <Route path="/products/categories" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ProductCategories /></DashboardLayout></ProtectedRoute>} />
              <Route path="/report/product" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ReportProduct /></DashboardLayout></ProtectedRoute>} />
              <Route path="/report/inventory" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ReportInventory /></DashboardLayout></ProtectedRoute>} />
              <Route path="/report/sales" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ReportSales /></DashboardLayout></ProtectedRoute>} />
              <Route path="/report/order" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ReportOrder /></DashboardLayout></ProtectedRoute>} />
              <Route path="/report/return" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ReportReturn /></DashboardLayout></ProtectedRoute>} />
              <Route path="/report/invoice-receipt" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><InvoiceReceipt /></DashboardLayout></ProtectedRoute>} />
              <Route path="/invoices" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><InvoiceReceipt /></DashboardLayout></ProtectedRoute>} />
              <Route path="/receipts" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ReceiptList /></DashboardLayout></ProtectedRoute>} />
              <Route path="/report/center" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ReportCenter /></DashboardLayout></ProtectedRoute>} />
              <Route path="/report/audit" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><AuditLogReport /></DashboardLayout></ProtectedRoute>} />
              <Route path="/email-template-builder" element={<ProtectedRoute><EmailTemplateBuilder /></ProtectedRoute>} />
              <Route path="/integration" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><Integration /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/email" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><EmailIntegration /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/woocommerce" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><WooCommerceIntegration /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/shopee" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ShopeeIntegration /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/tiktok" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><TiktokIntegration /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/waha" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><WahaIntegration /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/ladipage" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><LadipageIntegration /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/api" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ApiIntegration /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/webhook" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><WebhookIntegration /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/courier" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><CourierIntegration /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/courier/jnt" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><JnTIntegration /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/courier/dhl" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><DHLIntegration /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/courier/ninjavan" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><NinjaVanIntegration /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/courier/poslaju" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><PosLajuIntegration /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/courier/easyparcel" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><EasyParcelIntegration /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/courier/spx" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><SpxIntegration /></DashboardLayout></ProtectedRoute>} />

              <Route path="/storage" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><Storage /></DashboardLayout></ProtectedRoute>} />
              <Route path="/auto-send/email" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><AutoSendManagement type="email" /></DashboardLayout></ProtectedRoute>} />
              <Route path="/auto-send/waha" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><AutoSendManagement type="waha" /></DashboardLayout></ProtectedRoute>} />
              <Route path="/documentation" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><Documentation /></DashboardLayout></ProtectedRoute>} />
              <Route path="/help-support" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><HelpAndSupport /></DashboardLayout></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><Settings /></DashboardLayout></ProtectedRoute>} />
              <Route path="/system/cash-categories" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><SystemCategories /></DashboardLayout></ProtectedRoute>} />
              {/* Access Management — unified Users + Roles */}
              <Route path="/access-management" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><AccessManagement /></DashboardLayout></ProtectedRoute>} />
              {/* Legacy routes → redirect to Access Management */}
              <Route path="/staff-management" element={<Navigate to="/access-management" replace />} />
              <Route path="/staff-management/add-staff" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><AddStaff /></DashboardLayout></ProtectedRoute>} />
              <Route path="/staff-management/edit-staff/:id" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><EditStaff /></DashboardLayout></ProtectedRoute>} />

              {/* Module 1: CRM - SALES */}
              <Route path="/sales/analytics" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><SalesAnalytics /></DashboardLayout></ProtectedRoute>} />
              <Route path="/sales" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><SalesAnalytics /></DashboardLayout></ProtectedRoute>} />
              {/* Dedicated form pages for + New Quotation / Invoice / Receipt */}
              <Route path="/sales/new/:type" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><NewDocumentPage /></DashboardLayout></ProtectedRoute>} />
              {/* Unified All Documents listing — quotations + invoices + receipts in one table */}
              <Route path="/sales/documents" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><AllDocuments /></DashboardLayout></ProtectedRoute>} />
              <Route path="/quotations" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><Quotations /></DashboardLayout></ProtectedRoute>} />
              <Route path="/sales/quotation" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ComingSoon module="CRM - Sales" title="Quotation" /></DashboardLayout></ProtectedRoute>} />
              <Route path="/leads" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><Leads /></DashboardLayout></ProtectedRoute>} />
              {/* Legacy /customers route — keep Customers.tsx accessible for any deep links, but the sidebar entry is now Leads. */}
              <Route path="/customers" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><Customers /></DashboardLayout></ProtectedRoute>} />

              {/* Module 2: INVENTORY — Stock Management + Stock Adjustment */}
              <Route path="/inventory" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><InventoryAnalytics /></DashboardLayout></ProtectedRoute>} />
              <Route path="/inventory/analytics" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><InventoryAnalytics /></DashboardLayout></ProtectedRoute>} />
              <Route path="/inventory/stock-management" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><StockManagement /></DashboardLayout></ProtectedRoute>} />
              <Route path="/inventory/stock-adjustment" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><StockAdjustment /></DashboardLayout></ProtectedRoute>} />
              <Route path="/purchase" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><PurchaseOrders /></DashboardLayout></ProtectedRoute>} />
              <Route path="/erp" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><InventoryAnalytics /></DashboardLayout></ProtectedRoute>} />
              {/* Fulfilment merged into Order module v2 — redirect to To Process tab. */}
              <Route path="/fulfilment" element={<Navigate to="/orders?tab=to_process" replace />} />

              {/* Module 3: WEB ARCHITECT */}
              <Route path="/web/analytics" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><WebAnalytics /></DashboardLayout></ProtectedRoute>} />
              <Route path="/web/builder" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ComingSoon module="Web Architect" title="AI Website Builder" /></DashboardLayout></ProtectedRoute>} />
              <Route path="/web/pages" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><PagesLibrary /></DashboardLayout></ProtectedRoute>} />
              <Route path="/web/pages/builder/:id" element={<ProtectedRoute><XpertBuilder /></ProtectedRoute>} />
              <Route path="/web/forms" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><FormIntegrationSetup /></DashboardLayout></ProtectedRoute>} />
              <Route path="/web/domains" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ComingSoon module="Web Architect" title="Domains Manager" /></DashboardLayout></ProtectedRoute>} />
              <Route path="/web/seo" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ComingSoon module="Web Architect" title="SEOptimizer" /></DashboardLayout></ProtectedRoute>} />

              {/* Module 4: FINANCE */}
              <Route path="/finance" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><FinanceDashboard /></DashboardLayout></ProtectedRoute>} />
              <Route path="/finance/pnl" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ProfitLoss /></DashboardLayout></ProtectedRoute>} />
              <Route path="/finance/cashflow" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><CashFlow /></DashboardLayout></ProtectedRoute>} />
              {/* Legacy expense route — kept for direct links; sidebar removed */}
              <Route path="/finance/expenses" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><Expenses /></DashboardLayout></ProtectedRoute>} />

              {/* Module 6: REPORT — additions */}
              <Route path="/report/finance" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ComingSoon module="Report" title="Finance Reporting" /></DashboardLayout></ProtectedRoute>} />
              <Route path="/report/web" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ComingSoon module="Report" title="Web Reporting" /></DashboardLayout></ProtectedRoute>} />

              {/* Module 7: INTEGRATIONS — additions */}
              <Route path="/integration/payment" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><PaymentGatewayIntegration /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/marketplace" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ComingSoon module="Integrations" title="Marketplace" /></DashboardLayout></ProtectedRoute>} />
              <Route path="/integration/webbuilder" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ComingSoon module="Integrations" title="Web Builder" /></DashboardLayout></ProtectedRoute>} />

              {/* Module 8: SYSTEM */}
              <Route path="/role-management" element={<Navigate to="/access-management?tab=roles" replace />} />
              <Route path="/billing" element={<ProtectedRoute><DashboardLayout isOnline={isOnline}><ComingSoon module="System" title="Billing & Subscription" /></DashboardLayout></ProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>

      </Router>
    </ThemeProvider>
  );
};

export default App;
